import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { AuthLayout } from '../components/AppShell.js';
import { BusinessFields } from '../components/BusinessFields.js';
import { CountrySelect } from '../components/CountrySelect.js';
import { OfferLine, useLiveOffer } from '../components/OfferLine.js';
import { SignupTermsLine } from '../components/SignupTermsLine.js';
import { SubmitRefusalLine, useSubmitRefusal } from '../components/SubmitRefusal.js';
import { Alert, Button, TextField } from '../components/ui.js';
import { ApiError, apiFetch } from '../lib/api.js';
import { attributionFrom } from '../lib/attribution.js';
import { BRAND_CONTACT_EMAIL } from '../lib/brand.js';
import { DEFAULT_COUNTRY } from '../lib/countries.js';
import { offerFrom } from '../lib/offer.js';
import { useSession } from '../session/SessionProvider.js';

/** Mirrors the server's minimum; the server is still the authority. */
const MIN_PASSWORD_LENGTH = 10;

interface SignUpResponse {
  /**
   * True when the account was created but not signed in, because the address
   * has to be confirmed first (production). See C-7 / D-8.
   */
  emailVerificationRequired?: boolean;
}

/**
 * One email, not two — ruling D-1.
 *
 * Sign-up used to ask for a billing address and an account address, which is
 * two questions to answer with the same word in almost every case. The billing
 * address now defaults to the account address on the server, and an agency
 * separates them later on their Settings page if they ever need to.
 */
interface FormState {
  agencyName: string;
  country: string;
  yourName: string;
  email: string;
  password: string;
  /** D-16 rulings 1 and 2. Both required; the server refuses either missing. */
  businessWebsite: string;
  businessConfirmed: boolean;
}

const EMPTY: FormState = {
  agencyName: '',
  country: DEFAULT_COUNTRY,
  yourName: '',
  email: '',
  password: '',
  businessWebsite: '',
  businessConfirmed: false,
};

/** Maps a server field path (`agency.country`) to the form field it belongs to. */
const FIELD_MAP: Record<string, keyof FormState> = {
  'agency.name': 'agencyName',
  // The server derives the billing address from the account one, so a
  // complaint about it belongs on the field that produced it.
  'agency.billingEmail': 'email',
  'agency.country': 'country',
  'user.name': 'yourName',
  'user.email': 'email',
  'user.password': 'password',
  'agency.businessWebsite': 'businessWebsite',
  'agency.businessConfirmed': 'businessConfirmed',
};

export function SignUpPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { refresh } = useSession();
  /*
   * C-132. The ad click reference and utm values on the address this page was
   * opened at, read once. Sent with the form and nowhere else.
   */
  const { search } = useLocation();
  const [attribution] = useState(() => attributionFrom(search));
  /*
   * C-133. `?offer=LAUNCH-30`, read once like the ad parameters. The line shows
   * only once the server says the code is live. It also goes with the form, so
   * the confirmation link can carry it to whichever browser opens the email;
   * `useRememberOfferFromAddress` keeps it for Buy credits in this one.
   */
  const [offerCode] = useState(() => offerFrom(search));
  const liveOffer = useLiveOffer(offerCode);

  const [form, setForm] = useState<FormState>(EMPTY);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // WAIT-PL ruling 1: a refusal is shown at the button and moves to the field.
  const refusal = useSubmitRefusal();
  /** The address to confirm, once the account exists but is not signed in. */
  const [awaitingConfirmation, setAwaitingConfirmation] = useState<string | null>(null);

  function update(field: keyof FormState) {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      setForm((current) => ({ ...current, [field]: event.target.value }));
    };
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    refusal.clear();

    if (form.password.length < MIN_PASSWORD_LENGTH) {
      setFieldErrors({
        password: `Please use at least ${MIN_PASSWORD_LENGTH} characters.`,
      });
      refusal.refuseFields();
      return;
    }

    setBusy(true);
    try {
      const response = await apiFetch<SignUpResponse>('/api/signup', {
        method: 'POST',
        body: {
          agency: {
            name: form.agencyName.trim(),
            // No billingEmail: the server uses the account address (D-1).
            country: form.country.trim(),
            businessWebsite: form.businessWebsite.trim(),
            businessConfirmed: form.businessConfirmed,
          },
          user: {
            name: form.yourName.trim(),
            email: form.email.trim(),
            password: form.password,
          },
          ...(attribution ? { attribution } : {}),
          ...(offerCode ? { offer: offerCode } : {}),
        },
      });

      if (response.emailVerificationRequired === true) {
        // No session was issued, so there is nowhere to navigate to: sending
        // them to the dashboard would bounce straight back to sign-in.
        setAwaitingConfirmation(form.email.trim());
        return;
      }

      await refresh();
      void navigate('/', { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError) {
        const issues = caught.fieldIssues;
        if (issues.length > 0) {
          const mapped: Partial<Record<keyof FormState, string>> = {};
          for (const issue of issues) {
            const field = FIELD_MAP[issue.field];
            if (field) mapped[field] = issue.message;
          }
          setFieldErrors(mapped);
          setError('Please check the highlighted fields.');
          refusal.refuseFields();
        } else {
          setError(caught.message);
          refusal.refuse(caught.message);
        }
      } else {
        setError('We could not reach the server. Please try again.');
        refusal.refuse('We could not reach the server. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  if (awaitingConfirmation) {
    return (
      <AuthLayout
        title="Confirm your email address"
        subtitle="Your account is created. One more step."
        footer={
          <Link to="/sign-in" className="font-medium text-fg underline">
            Go to sign in
          </Link>
        }
      >
        {/*
          **C-66.** This was a green "We have sent a confirmation link", which is
          untrue during a mail outage: sign-up succeeds whether or not the email
          went (Item 2 of 5 September), and the page cannot know which. So it
          says where to look rather than claiming what happened, in an info
          alert rather than a success one, and names a person to write to.
        */}
        <Alert tone="info">
          Look in the inbox for <strong>{awaitingConfirmation}</strong> for a confirmation link.
          Open it and you will be signed in.
        </Alert>
        <p className="mt-4 text-sm text-fg-muted">
          Nothing within a few minutes? Check your spam folder, then try signing in and we will
          offer to send it again. If it still does not come, write to {BRAND_CONTACT_EMAIL} and we
          will sort it out.
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Create your agency account"
      subtitle="Free to open. Nothing to pay until you order."
      footer={
        <>
          Already have an account?{' '}
          <Link to="/sign-in" className="font-medium text-fg underline">
            Sign in
          </Link>
        </>
      }
    >
      <form
        ref={refusal.formRef}
        onSubmit={(event) => void handleSubmit(event)}
        className="space-y-5"
        noValidate
      >
        {liveOffer ? <OfferLine offer={liveOffer} /> : null}

        {error ? <Alert>{error}</Alert> : null}

        <TextField
          label="Agency name"
          name="agencyName"
          autoComplete="organization"
          required
          value={form.agencyName}
          onChange={update('agencyName')}
          error={fieldErrors.agencyName}
        />

        <CountrySelect
          label="Country"
          hint="Start typing to find it."
          name="country"
          value={form.country}
          onChange={(code) => setForm((current) => ({ ...current, country: code }))}
          error={fieldErrors.country}
        />

        <BusinessFields
          website={form.businessWebsite}
          onWebsiteChange={(value) => setForm((current) => ({ ...current, businessWebsite: value }))}
          confirmed={form.businessConfirmed}
          onConfirmedChange={(value) =>
            setForm((current) => ({ ...current, businessConfirmed: value }))
          }
          websiteError={fieldErrors.businessWebsite}
          confirmedError={fieldErrors.businessConfirmed}
        />

        <hr className="border-rule" />

        <TextField
          label="Your name"
          name="yourName"
          autoComplete="name"
          required
          value={form.yourName}
          onChange={update('yourName')}
          error={fieldErrors.yourName}
        />

        <TextField
          label="Your email address"
          hint="You will sign in with this, and invoices go here too. You can change where invoices go later, in Settings."
          type="email"
          name="email"
          autoComplete="email"
          required
          value={form.email}
          onChange={update('email')}
          error={fieldErrors.email}
        />

        <TextField
          label="Password"
          hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}
          type="password"
          name="password"
          autoComplete="new-password"
          required
          value={form.password}
          onChange={update('password')}
          error={fieldErrors.password}
        />

        <SubmitRefusalLine message={refusal.message} />

        <Button type="submit" busy={busy} className="w-full">
          Create account
        </Button>

        {/*
          **WAIT-B round 2, item 2.** D-14 ruling 9 and D-15 item 3 both put
          this line under every button that creates an account, and this is the
          button. It was only ever on the invite flow's `SetPasswordPage`, so
          the public sign-up screen had no Terms link at all except the
          footer's, which went to the portal's own forwarding stub.
          Meanwhile `signup/service.ts` has recorded `termsVersion` against
          every one of these sign-ups: the record said "agreed to the Terms" on
          a screen that never offered them. That is the part that made this
          worth fixing before the phase ships rather than after.
        */}
        <SignupTermsLine />
      </form>
    </AuthLayout>
  );
}
